import { useAuthContext } from '@/auth/useAuthContext';
import { type SpaceNounForms, resolveSpaceNounForms } from '@/lib/spaceNoun';
import { useMemo } from 'react';

/**
 * Staff-side hook: the tenant's configured "space" noun (run / pen / suite…)
 * in four forms — `{ singular, plural, Singular, Plural }`.
 *
 * Reads the resolved pair from `/api/me` (`spaceNoun`), falling back to the
 * raw account columns, then to the platform default.
 *
 * Safe to call outside `AuthProvider` (returns the default): `useAuthContext`
 * always registers exactly one `useContext` before it throws, so catching
 * keeps the hook order stable across renders. This also keeps the many tests
 * that mock `@/auth/useAuthContext` with only `useAuthContext` working.
 */
export function useSpaceNoun(): SpaceNounForms {
  let me: ReturnType<typeof useAuthContext>['state']['me'] | null = null;
  try {
    me = useAuthContext().state.me;
  } catch {
    me = null;
  }
  const resolved = me?.spaceNoun ?? null;
  const account = me?.activeAccount ?? null;
  const singular = resolved?.singular ?? account?.spaceNounSingular ?? null;
  const plural = resolved?.plural ?? account?.spaceNounPlural ?? null;
  return useMemo(() => resolveSpaceNounForms({ singular, plural }), [singular, plural]);
}
